import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {
  Box, ButtonBase, IconButton, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  ArrowLeft01Icon, ArrowRight01Icon, CallIcon, Calendar03Icon, Cancel01Icon, Message02Icon,
} from "@hugeicons/core-free-icons";
import {useAtom} from "jotai";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {useDebounce} from "../../../../hooks/useDebounce";
import {useStompWebsocket} from "../../../../hooks/useStompWebsocket";
import {useFlowProtection} from "../../../../hooks/useFlowProtection";
import {SelectedConversationAtom} from "../../../project/dashboard/atoms";
import CompactAudioPlayer from "../../../project/dashboard/components/CompactAudioPlayer";
import {DateRangePickerMenu} from "./DateRangePickerMenu";
import {Panel, PanelHeading} from "./Panel";
import {csvFileName, downloadBlob, ExportButton} from "./ExportButton";
import {toast} from "react-toastify";
import {ConversationModal} from "./ConversationModal";
import {
  AVATAR_COLORS, formatStatus, initialsOf, LIVE_ROW_SX, LiveAvatar, SenderName, StatusBadge, TagPills, TransferNumber
} from "./conversationUi";
import {headLabelSx, SearchHeadFilter, SelectHeadFilter} from "./HeaderFilters";
import {subProjectLabel} from "../../../../utils/subProjects";

const PAGE_SIZE = 10;

const VOICE_STATUSES = [
  "ACTIVE", "FINISHED", "SUCCESS", "REDIRECTED", "HANDOVER", "HANGUP", "MISSED_CALL", "FAILED", "USER_NOT_TALKING"
];
const CHAT_STATUSES = ["ACTIVE", "FINISHED", "FAILED"];

const headCellSx = {
  height: 40,
  py: 0.5, px: 1.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0",
  bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};

const cellSx = {py: 0.75, px: 1.5, fontSize: 13, borderBottom: "1px solid #F2F2F7"};

// Latest conversations of the project, filtered from the column headers, with paging.
// subProjectId: the dashboard's language, the language filter starts on it (and follows it when it changes).
// projectVersionType: the dashboard's version type filter (PRODUCTION / STAGE, null = every version).
// subProjects: the project's languages; flows: {flowUid: Firebase flow} of those languages, for tag names,
// success tags and contacts.
export const ConversationsPanel = ({
  projectId, subProjectId, projectVersionType, subProjects, flows, conversationType, refreshInterval,
}) => {
  const [selectedConversation, setSelectedConversation] = useAtom(SelectedConversationAtom);
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  // Column filters: search = caller, or "#id" for a conversation id; start / end: JS Dates of the date range
  // subProjectId: "" = every language
  const [filters, setFilters] = useState({
    search: "", subProjectId: subProjectId ? String(subProjectId) : "", start: null, end: null, dateLabel: null,
    status: "", tagIds: [],
  });
  const setFilter = (key, value) => setFilters(prev => ({...prev, [key]: value}));
  useEffect(() => {
    setFilters(prev => ({...prev, subProjectId: subProjectId ? String(subProjectId) : ""}));
  }, [subProjectId]);
  const filtersDebounced = useDebounce(filters, 400);
  const requestRef = useRef(0);

  const isVoice = conversationType !== "CHAT_BOT";
  const statuses = isVoice ? VOICE_STATUSES : CHAT_STATUSES;

  // Tags of every language's flow, by uid
  const tagNames = useMemo(() => Object.fromEntries(Object.values(flows || {}).flatMap(flow =>
    (flow?.settings?.tags || []).filter(tag => tag?.id).map(tag => [tag.id, tag.title || tag.name || tag.id])
  )), [flows]);
  const tagOptions = useMemo(() => Object.entries(tagNames).map(([value, label]) => ({value, label})), [tagNames]);

  const languageOptions = useMemo(() => (subProjects || []).map(item => ({
    value: String(item.id), label: subProjectLabel(item),
  })), [subProjects]);

  // A conversation's language: by its sub project id, else by the flow it ran on
  const subProjectOf = (conversation) => (subProjects || []).find(item =>
    (conversation?.subProjectId != null && String(item.id) === String(conversation.subProjectId))
    || (conversation?.flowUid && item.flowUid === conversation.flowUid)
  );
  const flowOf = (conversation) => flows?.[conversation?.flowUid || subProjectOf(conversation)?.flowUid];

  // Query of the list and of the CSV export for the given column filters
  const toQuery = useCallback((columnFilters) => {
    const search = columnFilters.search.trim();
    const isIdSearch = search.startsWith("#");
    return {
      projectId,
      subProjectId: columnFilters.subProjectId || undefined,
      projectVersionType: projectVersionType || undefined,
      type: conversationType,
      id: isIdSearch ? search.slice(1).trim() || undefined : undefined,
      caller: !isIdSearch ? search || undefined : undefined,
      start: columnFilters.start ? columnFilters.start.getTime() : undefined,
      end: columnFilters.end ? columnFilters.end.getTime() : undefined,
      status: columnFilters.status || undefined,
      tagIds: columnFilters.tagIds.length ? columnFilters.tagIds : undefined,
    };
  }, [projectId, projectVersionType, conversationType]);

  // a new unlock token (protected messages unlocked or locked again) reloads the page: senders are masked without it
  const {token: protectionToken} = useFlowProtection();

  // signal: aborts it (the refresh on active-call changes)
  const load = useCallback(async (signal) => {
    if (!projectId) return;
    const requestId = ++requestRef.current;
    try {
      const response = await ConversationRepository.getConversations({
        ...toQuery(filtersDebounced),
        page,
        size: PAGE_SIZE,
        signal: signal instanceof AbortSignal ? signal : undefined,
      });
      if (requestId !== requestRef.current) return;
      setConversations(response?.data?.content || []);
      setTotal(response?.data?.totalElements || 0);
    } catch (e) {
      if (e?.name !== "CanceledError" && e?.name !== "AbortError") console.error("Error fetching conversations:", e);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, toQuery, page, filtersDebounced, protectionToken]);

  // The number of active conversations changed (a call started or ended): reload the current page. Only one such
  // reload runs at a time; changes that arrive while it is still fetching are ignored.
  const activeTopic = filters.subProjectId
    ? `/topic/sub-project/${filters.subProjectId}/active-conversations`
    : projectId ? `/topic/project/${projectId}/active-conversations` : null;
  const [activeUpdate] = useStompWebsocket(activeTopic);
  const activeRefreshRef = useRef(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!activeUpdate || activeRefreshRef.current) return;
    if (conversationType && activeUpdate.conversationType && activeUpdate.conversationType !== conversationType) return;
    const controller = new AbortController();
    activeRefreshRef.current = controller;
    loadRef.current(controller.signal).finally(() => {
      if (activeRefreshRef.current === controller) activeRefreshRef.current = null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUpdate]);
  useEffect(() => () => activeRefreshRef.current?.abort(), []);

  // Conversations created or changed, pushed for the filtered language (or the whole project). A conversation that
  // is on the current page (or open in the modal) gets the new details; its sender is kept, as the pushed one is masked.
  const conversationsTopic = filters.subProjectId
    ? `/topic/sub-project/${filters.subProjectId}/conversations`
    : projectId ? `/topic/project/${projectId}/conversations` : null;
  const [conversationUpdate] = useStompWebsocket(conversationsTopic);
  useEffect(() => {
    if (!conversationUpdate?.id) return;
    const merge = (current) => ({
      ...current,
      ...conversationUpdate,
      sender: current.sender,
      senderName: current.senderName ?? conversationUpdate.senderName,
    });
    setConversations(list => list.some(item => item.id === conversationUpdate.id)
      ? list.map(item => item.id === conversationUpdate.id ? merge(item) : item)
      : list);
    setSelectedConversation(current => current?.id === conversationUpdate.id ? merge(current) : current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationUpdate]);

  // Every conversation matching the current filters, built by the backend as CSV
  const [exporting, setExporting] = useState(false);
  const exportConversations = async () => {
    setExporting(true);
    try {
      const response = await ConversationRepository.exportConversations(toQuery(filters));
      const disposition = response?.headers?.["content-disposition"] || "";
      const fileName = disposition.match(/filename="?([^";]+)"?/)?.[1] || csvFileName(`conversations-project-${projectId}`);
      downloadBlob(response.data, fileName);
    } catch (e) {
      console.error("Error exporting conversations:", e);
      toast.error("Failed to export conversations");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshInterval) return;
    const intervalId = setInterval(load, refreshInterval);
    return () => clearInterval(intervalId);
  }, [load, refreshInterval]);

  // a new search / filter starts on the first page
  useEffect(() => {
    setPage(0);
  }, [filtersDebounced]);

  // recording by conversation id, downloaded on the first play (the list does not say whether there is one: the
  // player shows "Audio not available" when the download fails). An active call has no recording yet.
  const audioRequest = (conversation) => conversation?.id && conversation.status !== "ACTIVE"
    ? ({signal}) => ConversationRepository.getConversationAudio({
      id: conversation.id, flowUid: conversation.flowUid || subProjectOf(conversation)?.flowUid, signal,
    })
    : null;

  const conversationTags = (conversation) => {
    const successTag = tagNames[flowOf(conversation)?.configuration?.success_tag];
    if (conversation?.status === "SUCCESS" && successTag) return [successTag];
    return (conversation?.tags || []).map(id => tagNames[id]).filter(Boolean);
  };

  const columns = isVoice ? 6 : 5;
  const hasFilters = Boolean(
    filters.search || filters.subProjectId || filters.start || filters.status || filters.tagIds.length
  );
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <Panel id={"conversations"} sx={{overflow: "hidden"}}>
      <Box sx={{p: 2.5, pb: 0}}>
        <PanelHeading
          kicker={"RECENT ACTIVITY"}
          title={"Latest conversations"}
          count={total}
          description={"Keep a pulse on every interaction your agent handles."}
          actions={
            <ExportButton
              title={"Export conversations (CSV)"}
              loading={exporting}
              disabled={total === 0}
              onClick={exportConversations}
            />
          }
        />
      </Box>

      <TableContainer sx={{overflowX: "auto"}}>
        <Table size={"small"} sx={{minWidth: 640}}>
          <TableHead>
            <TableRow>
              <TableCell sx={{...headCellSx, pl: 2.5}}>
                <SearchHeadFilter
                  label={"CONVERSATION"}
                  placeholder={"Caller or #id"}
                  value={filters.search}
                  onChange={(value) => setFilter("search", value)}
                />
              </TableCell>
              <TableCell sx={headCellSx}>
                <SelectHeadFilter
                  label={"LANGUAGE"}
                  value={filters.subProjectId}
                  options={languageOptions}
                  onChange={(value) => setFilter("subProjectId", value)}
                />
              </TableCell>
              <TableCell sx={headCellSx}>
                <DateRangePickerMenu
                  value={filters.start && filters.end ? {start: filters.start, end: filters.end, label: filters.dateLabel} : null}
                  onChange={(next) => setFilters(prev => (next
                    ? {...prev, start: next.start.toDate(), end: next.end.toDate(), dateLabel: next.label}
                    : {...prev, start: null, end: null, dateLabel: null}))}
                  ariaLabel={"Filter by date & time"}
                  renderTrigger={({active, onClick, onClear}) => (
                    <ButtonBase
                      onClick={onClick}
                      sx={{...headLabelSx(active), gap: 0.5, textTransform: "none", whiteSpace: "nowrap"}}
                      aria-label={"Filter by date & time"}
                    >
                      <HugeiconsIcon icon={Calendar03Icon} size={12}/>
                      <Box component={"span"} sx={{fontSize: active ? 12 : 11, letterSpacing: active ? 0 : undefined}}>
                        {active
                          ? `${moment(filters.start).format("DD MMM")} – ${moment(filters.end).format("DD MMM")}`
                          : "DATE & TIME"}
                      </Box>
                      {active && (
                        <HugeiconsIcon
                          icon={Cancel01Icon}
                          size={12}
                          onClick={(e) => {e.stopPropagation(); onClear();}}
                        />
                      )}
                    </ButtonBase>
                  )}
                />
              </TableCell>
              <TableCell sx={headCellSx}>
                <SelectHeadFilter
                  label={"STATUS"}
                  value={filters.status}
                  options={statuses.map(value => ({value, label: formatStatus(value)}))}
                  onChange={(value) => setFilter("status", value)}
                />
              </TableCell>
              <TableCell sx={{...headCellSx, pr: isVoice ? 1.5 : 2.5}}>
                <SelectHeadFilter
                  multiple
                  label={"TAGS"}
                  value={filters.tagIds}
                  options={tagOptions}
                  onChange={(value) => setFilter("tagIds", value)}
                />
              </TableCell>
              {isVoice && <TableCell padding={"checkbox"} sx={{...headCellSx, pr: 2.5}}>DURATION</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && conversations.length === 0 && Array.from({length: 5}).map((_, index) => (
              <TableRow key={index}>
                <TableCell colSpan={columns} sx={{...cellSx, px: 2.5}}><Skeleton height={28}/></TableCell>
              </TableRow>
            ))}
            {conversations.map((conversation, index) => {
              const name = conversation?.senderName || flowOf(conversation)?.phonebook?.[conversation?.sender]
                || conversation?.sender;
              const language = subProjectOf(conversation);
              const initials = initialsOf(name);
              const avatar = AVATAR_COLORS[(page * PAGE_SIZE + index) % AVATAR_COLORS.length];
              const date = moment(conversation?.dateCreated);
              const isLive = conversation?.status === "ACTIVE";
              return (
                <TableRow
                  key={conversation.id}
                  hover
                  tabIndex={0}
                  onClick={() => setSelectedConversation(conversation)}
                  onKeyDown={(e) => e.key === "Enter" && setSelectedConversation(conversation)}
                  aria-label={`View conversation with ${name || conversation.id}`}
                  sx={{
                    cursor: "pointer", "&.MuiTableRow-hover:hover": {bgcolor: "#FAFAFD"}, "&:last-of-type td": {borderBottom: 0},
                    ...(isLive ? LIVE_ROW_SX : {}),
                  }}
                >
                  <TableCell sx={{...cellSx, pl: 2.5}}>
                    <Stack direction={"row"} alignItems={"center"} gap={1.25} sx={{minWidth: 0}}>
                      {isLive ? <LiveAvatar isVoice={isVoice}/> : (
                        <Box sx={{
                          width: 28, height: 28, borderRadius: "50%", flexShrink: 0, display: "flex",
                          alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600,
                          bgcolor: avatar.bg, color: avatar.color,
                        }}>
                          {initials || <HugeiconsIcon icon={isVoice ? CallIcon : Message02Icon} size={14}/>}
                        </Box>
                      )}
                      <Box sx={{minWidth: 0}}>
                        <Stack direction={"row"} alignItems={"center"} gap={0.75} sx={{minWidth: 0}}>
                          <SenderName name={name} sender={conversation?.sender}
                                      sx={{fontSize: 13, fontWeight: isLive ? 700 : 600, color: "#23264D", lineHeight: 1.3}}/>
                          <TransferNumber conversation={conversation}/>
                        </Stack>
                        <Typography noWrap sx={{fontSize: 11, color: "#8A8AA0", lineHeight: 1.3}}>
                          #{conversation.id}
                          {isLive && (
                            <Box component={"span"} sx={{color: "#4B55C9", fontWeight: 600}}>
                              {` · ${isVoice ? "Call in progress" : "Chat in progress"}`}
                            </Box>
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{...cellSx, whiteSpace: "nowrap"}}>
                    {language ? (
                      <Tooltip title={language.languageName || ""}>
                        <Box component={"span"} sx={{
                          px: 0.75, py: 0.25, borderRadius: "6px", fontSize: 11, fontWeight: 600,
                          letterSpacing: "0.04em", textTransform: "uppercase", color: "#4A4A63", bgcolor: "#F2F2F7",
                        }}>
                          {language.languageCode || language.languageName}
                        </Box>
                      </Tooltip>
                    ) : <Typography sx={{fontSize: 12, color: "#B4B4C3"}}>—</Typography>}
                  </TableCell>
                  <TableCell sx={{...cellSx, whiteSpace: "nowrap"}}>
                    <Typography sx={{fontSize: 13, color: "#23264D", lineHeight: 1.3}}>{date.format("DD MMM YYYY")}</Typography>
                    <Typography sx={{fontSize: 11, color: "#8A8AA0", lineHeight: 1.3}}>{date.format("HH:mm")}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx}><StatusBadge conversation={conversation}/></TableCell>
                  <TableCell sx={{...cellSx, pr: isVoice ? 1.5 : 2.5, maxWidth: 180}}>
                    <TagPills names={conversationTags(conversation)}/>
                  </TableCell>
                  {isVoice && (
                    <TableCell padding={"checkbox"} sx={{...cellSx, pr: 2.5}} onClick={(e) => e.stopPropagation()}>
                      <CompactAudioPlayer audioKey={`conversation-${conversation.id}`} request={audioRequest(conversation)}/>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {!loading && conversations.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns} sx={{...cellSx, borderBottom: 0, py: 5, textAlign: "center", color: "#8A8AA0"}}>
                  {hasFilters
                    ? "No conversations match these filters."
                    : "No conversations yet."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}
             sx={{px: 2.5, py: 1.25, borderTop: "1px solid #ECECF3", mt: "auto"}}>
        <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
          Showing {from}–{to} of {total.toLocaleString()} conversations
        </Typography>
        <Stack direction={"row"} gap={0.5}>
          <IconButton size={"small"} disabled={page === 0} onClick={() => setPage(page - 1)} aria-label={"Previous page"}>
            <HugeiconsIcon icon={ArrowLeft01Icon} size={16}/>
          </IconButton>
          <IconButton size={"small"} disabled={to >= total} onClick={() => setPage(page + 1)} aria-label={"Next page"}>
            <HugeiconsIcon icon={ArrowRight01Icon} size={16}/>
          </IconButton>
        </Stack>
      </Stack>
      <ConversationModal
        flow={selectedConversation ? flowOf(selectedConversation) : null}
        subProjectOf={subProjectOf}
        tagsOf={conversationTags}
      />
    </Panel>
  );
};
